import { string, date, mixed, setLocale } from 'yup';
import { subYears } from 'date-fns';
import i18next from 'i18next';
import Backend from 'i18next-xhr-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

var DOB_MAX_AGE = 100;
var DOB_MIN_AGE = 13;
var NAME_MAX_LENGTH = 100;
var NAME_REGEXP = /^[A-Za-z\u00c0-\u00ff\u0027]+(?:[ _-][A-Za-z\u00c0-\u00ff\u0027]+)*$/;
var PASSWORD_MIN_LENGTH = 8;
var HOUSENUMBER_MAX_LENGTH = 10;
var ZIPCODE_MAX_LENGTH = 10;
var TELEPHONE_REGEXP = /^(\+?[\d\-\s]+)$/;
var LINKED_IN_REGEX = /linkedin/i;
var NL_LOCALE = 'nl';
/* LABELS */

var LINKED_IN_LABEL = 'linked_in';

var currentYear = new Date().getFullYear();
var maxDateOfBirthNetherlands = subYears(new Date(currentYear, 0, 1), DOB_MIN_AGE - 1);
var maxDateOfBirthGeneral = subYears(new Date(), DOB_MIN_AGE);

var maxDateOfBirth = function maxDateOfBirth(locale) {
  var isDutchSite = locale === NL_LOCALE;
  return isDutchSite ? maxDateOfBirthNetherlands : maxDateOfBirthGeneral;
};

var validName = function validName() {
  return string().label('name').trim().max(NAME_MAX_LENGTH).matches(NAME_REGEXP);
};
var validCandidateAge = function validCandidateAge(locale) {
  return date().label('candidate_age').max(maxDateOfBirth(locale)).min(subYears(new Date(), DOB_MAX_AGE));
};
var validEndDate = function validEndDate(startDate) {
  return string().label('valid_end_date').test({
    name: 'validEndDate',
    test: function test(value) {
      // See https://github.com/jquense/yup/issues/423 for more info about resolve
      var startDateRef = this.resolve(startDate);
      var startTimestamp = Date.parse(startDateRef);
      var endTimestamp = Date.parse(value);
      var endDateAfterStartDate = endTimestamp >= startTimestamp;
      return startDateRef && value ? endDateAfterStartDate : true;
    }
  }).transform(function (value) {
    return value.toString();
  });
};
var validPassword = function validPassword() {
  return string().min(PASSWORD_MIN_LENGTH);
};
var validZipcode = function validZipcode() {
  return string().max(ZIPCODE_MAX_LENGTH);
};
var validHousenumber = function validHousenumber() {
  return string().max(HOUSENUMBER_MAX_LENGTH);
};
var validTelephone = function validTelephone() {
  return string().label('telephone').trim().matches(TELEPHONE_REGEXP);
};
var validMinimumAge = function validMinimumAge() {
  return mixed().label('minimum_age').oneOf([true]);
};
var validTermsResponse = function validTermsResponse() {
  return mixed().label('terms_agreement').oneOf([true]);
};
var validLinkedInUrl = function validLinkedInUrl() {
  return string().label(LINKED_IN_LABEL).url().matches(LINKED_IN_REGEX, {
    excludeEmptyString: true
  });
};
var validUniqueEmail = function validUniqueEmail(uniqueEmailChecker) {
  return string().email().label('email_not_unique').test({
    name: 'emailNotUnique',
    test: function test(email) {
      return uniqueEmailChecker(email);
    }
  });
};

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

var Context = function Context() {
  var _this = this;

  _classCallCheck(this, Context);

  this.instance = null;

  this.getInstance = function () {
    if (!_this.instance) {
      throw new ReferenceError('getInstance was called before setInstance');
    }

    return _this.instance;
  };

  this.setInstance = function (inst) {
    _this.instance = inst;
  };

  this.t = function () {
    var _this$getInstance;

    return (_this$getInstance = _this.getInstance()).t.apply(_this$getInstance, arguments);
  };
};

var _Context = new Context(),
    setInstance = _Context.setInstance,
    t = _Context.t;

function initLocales() {
  setLocale({
    string: {
      email: function email() {
        return t('email_invalid');
      },
      max: function max(_ref) {
        var _max = _ref.max;
        return t('text_too_long', {
          max: _max
        });
      },
      min: function min(_ref2) {
        var _min = _ref2.min;
        return t('text_too_short', {
          min: _min
        });
      },
      url: function url() {
        return t('url_invalid');
      },
      matches: function matches(_ref3) {
        var regex = _ref3.regex,
            label = _ref3.label;
        var type = label || 'text';
        return t("".concat(type, "_invalid"), {
          regex: regex
        });
      }
    },
    date: {
      min: function min(_ref4) {
        var label = _ref4.label,
            _min2 = _ref4.min;

        if (label === 'candidate_age') {
          return t('candidate_age_too_old', {
            maxAge: DOB_MAX_AGE
          });
        }

        return t('date_too_early', {
          min: _min2
        });
      },
      max: function max(_ref5) {
        var label = _ref5.label,
            _max2 = _ref5.max;

        if (label === 'candidate_age') {
          return t('candidate_age_too_young', {
            minAge: DOB_MIN_AGE
          });
        }

        return t('date_too_late', {
          max: _max2
        });
      }
    },
    mixed: {
      oneOf: function oneOf(_ref6) {
        var label = _ref6.label,
            values = _ref6.values;

        if (label === 'terms_agreement') {
          return t('terms_agreement');
        }

        if (label === 'minimum_age') {
          return t('minimum_age');
        }

        return t('must_be_one_of', {
          values: values
        });
      },
      default: function _default(_ref7) {
        var label = _ref7.label;

        if (label === 'valid_end_date') {
          return t('end_date_before_start_date');
        }

        if (label === 'email_not_unique') {
          return t('email_not_unique');
        }

        return t('invalid');
      },
      required: function required() {
        return t('required');
      }
    },
    array: {
      min: function min(_ref8) {
        var _min3 = _ref8.min;
        return t('items_too_few', {
          min: _min3
        });
      }
    }
  });
}

/* eslint import/prefer-default-export: off */
var IS_PRODUCTION = "production" === 'production';
var BACKEND_URL = "https://storage.googleapis.com/".concat("yc-static-production/form-validation-locales");
var initValidationsI18next = function initValidationsI18next() {
  return {
    type: '3rdParty',
    init: function init() {
      initLocales();
      var instance = i18next.createInstance().use(Backend).use(LanguageDetector);
      setInstance(instance);
      return instance.init({
        detection: {
          order: ['htmlTag'],
          caches: ['localStorage']
        },
        fallbackLng: ['en', 'dev'],
        interpolation: {
          formatSeparator: ','
        },
        returnObjects: true,
        debug: !IS_PRODUCTION,
        backend: {
          loadPath: "".concat(BACKEND_URL, "/{{lng}}.json")
        }
      });
    }
  };
};

export { initValidationsI18next, validName, validCandidateAge, validEndDate, validPassword, validZipcode, validHousenumber, validTelephone, validMinimumAge, validTermsResponse, validLinkedInUrl, validUniqueEmail, DOB_MAX_AGE, DOB_MIN_AGE, NAME_MAX_LENGTH, NAME_REGEXP, PASSWORD_MIN_LENGTH, HOUSENUMBER_MAX_LENGTH, ZIPCODE_MAX_LENGTH, TELEPHONE_REGEXP, LINKED_IN_REGEX, NL_LOCALE, LINKED_IN_LABEL };
